import "./sidebar.scss";
import DashboardIcon from "@mui/icons-material/Dashboard";
import ArchiveIcon from "@mui/icons-material/Archive";
import ExitToAppIcon from "@mui/icons-material/ExitToApp";
import SettingsIcon from "@mui/icons-material/Settings";
import ManageAccountsIcon from "@mui/icons-material/ManageAccounts";
import AddBusinessIcon from "@mui/icons-material/AddBusiness";
import StorefrontIcon from "@mui/icons-material/Storefront";
import LocationCityIcon from "@mui/icons-material/LocationCity";
import WebIcon from "@mui/icons-material/Web";
import ListAltIcon from "@mui/icons-material/ListAlt";
import CategoryIcon from "@mui/icons-material/Category";
import ArticleIcon from "@mui/icons-material/Article";
import ColorLensIcon from "@mui/icons-material/ColorLens";
import StraightenIcon from "@mui/icons-material/Straighten";
import WhereToVoteIcon from "@mui/icons-material/WhereToVote";
import PhonelinkSetupIcon from "@mui/icons-material/PhonelinkSetup";
import ImportantDevicesIcon from "@mui/icons-material/ImportantDevices";
import DiscountIcon from "@mui/icons-material/Discount";
import PersonPinCircleIcon from "@mui/icons-material/PersonPinCircle";
import RoundaboutRightIcon from "@mui/icons-material/RoundaboutRight";
import PlaylistAddCheckCircleIcon from "@mui/icons-material/PlaylistAddCheckCircle";
import ManageSearchIcon from "@mui/icons-material/ManageSearch";
import PriceCheckIcon from "@mui/icons-material/PriceCheck";
import InventoryIcon from "@mui/icons-material/Inventory";
import ViewDayIcon from "@mui/icons-material/ViewDay";
import ViewCarouselIcon from "@mui/icons-material/ViewCarousel";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";

import { HiOutlineDocumentReport } from "react-icons/hi";
import { FaUserFriends, FaBoxes } from "react-icons/fa";

import { DarkModeContext } from "../../context/darkModeContext";
import { useContext, useEffect, useState } from "react";
import { AuthContext } from "../../context/AuthContext";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { getMe } from "../../services/UserServices";
import useDocumentTitle from "../Title";

// ─── NavItem ──────────────────────────────────────────────────────────────────

const NavItem = ({ to, icon: Icon, label, selected, iconProps = {} }) => (
  <Link to={to} style={{ textDecoration: "none" }}>
    <li className={selected ? "selected" : ""}>
      <Icon className="icon2" {...iconProps} />
      <span>{label}</span>
    </li>
  </Link>
);

// ─── ModuleItem (top-level expandable) ───────────────────────────────────────

const ModuleItem = ({ icon: Icon, label, moduleKey, isOpen, onClick, className = "", iconProps = {} }) => (
  <li
    onClick={onClick}
    className={`menu-item ${className} ${isOpen ? "open" : ""}`}
  >
    <Icon className="icon5" {...iconProps} />
    <span>{label}</span>
    <KeyboardArrowRightIcon className={`chevron ${isOpen ? "chevron--open" : ""}`} />
  </li>
);

// ─── Sidebar ──────────────────────────────────────────────────────────────────

function Sidebar({ visible }) {
  const { dispatch } = useContext(DarkModeContext);
  const { logout, isLoadEmpresa } = useContext(AuthContext);
  const [user, setUser] = useState(null);
  const [acciones, setAcciones] = useState([]);

  const navigate = useNavigate();
  useDocumentTitle("Panel De Control");

  useEffect(() => {
    const initApp = async () => {
      const userMe = await getMe();
      localStorage.setItem("paneluserData", JSON.stringify(userMe));
      setUser(userMe);
    };
    initApp();
  }, []);

  useEffect(() => {
    if (!user) return;
    const permisos = Array.isArray(user.emplAcciones)
      ? user.emplAcciones.map((a) => a.permiso)
      : [];
    setAcciones(permisos);
  }, [user]);

  const can = (p) => acciones.includes("read:" + p);
  const option = (p) => acciones.includes("option:" + p);
  const menu = (p) => acciones.includes("module:" + p);

  const location = useLocation();
  const pathname = location.pathname;

  // Mapa ruta → clave de ítem
  const routeToKey = {
    "/": "inicio",
    "/articulos": "articulosGestion",
    "/ofertas": "ofertas",
    "/inventarios": "inventarios",
    "/ramos": "ramos",
    "/rubros": "rubros",
    "/subrubros": "subrubros",
    "/marcas": "marcas",
    "/colores": "colores",
    "/medidas": "medidas",
    "/modelos": "modelos",
    "/presentaciones": "presentaciones",
    "/talles": "talles",
    "/temporadas": "temporadas",
    "/clientes": "cliente",
    "/pedidos": "pedidos",
    "/pedidosHistorial": "historial",
    "/empleados": "empleados_list",
    "/roles": "roles",
    "/empresa/new": "empresa",
    "/empresa/new/1": "empresa",
    "/sucursales": "sucursales",
    "/depositos": "depositos",
    "/provincias": "provincias",
    "/departamentos": "departamentos",
    "/localidades": "localidades",
    "/banners": "banners",
    "/sliders": "sliders",
    "/descuentos": "descuentos",
    "/existencias": "existencias",
    "/visitas": "visitas",
    "/visitasmaps": "rutas",
  };

  // Módulo que debe estar abierto según la ruta actual
  const routeToModule = {
    "/articulos": "articulos",
    "/ofertas": "articulos",
    "/inventarios": "articulos",
    "/ramos": "articulos",
    "/rubros": "articulos",
    "/subrubros": "articulos",
    "/marcas": "articulos",
    "/colores": "articulos",
    "/medidas": "articulos",
    "/modelos": "articulos",
    "/presentaciones": "articulos",
    "/talles": "articulos",
    "/temporadas": "articulos",
    "/clientes": "clientes",
    "/pedidos": "clientes",
    "/pedidosHistorial": "clientes",
    "/empleados": "empleados",
    "/roles": "empleados",
    "/empresa/new": "configuracion",
    "/empresa/new/1": "configuracion",
    "/sucursales": "configuracion",
    "/depositos": "configuracion",
    "/provincias": "configuracion",
    "/departamentos": "configuracion",
    "/localidades": "configuracion",
    "/banners": "webecommerce",
    "/sliders": "webecommerce",
    "/descuentos": "webecommerce",
    "/existencias": "informes",
    "/visitas": "webpreventa",
    "/visitasmaps": "webpreventa",
  };

  // Submodulo que debe estar abierto según la ruta
  const routeToSubModule = {
    "/ramos": "categorias",
    "/rubros": "categorias",
    "/subrubros": "categorias",
    "/marcas": "categorias",
    "/colores": "caracteristicas",
    "/medidas": "caracteristicas",
    "/modelos": "caracteristicas",
    "/presentaciones": "caracteristicas",
    "/talles": "caracteristicas",
    "/temporadas": "caracteristicas",
  };

  const activeKey = routeToKey[pathname] ?? "";
  const activeModule = routeToModule[pathname] ?? null;
  const activeSubMod = routeToSubModule[pathname] ?? null;

  const [openModule, setOpenModule] = useState(activeModule);
  const [openSubModule, setOpenSubModule] = useState(activeSubMod);

  // Sincronizar apertura de módulos cuando cambia la ruta
  useEffect(() => {
    if (activeModule) setOpenModule(activeModule);
    if (activeSubMod) setOpenSubModule(activeSubMod);
  }, [pathname]);

  const toggleModule = (mod) => {
    setOpenModule((prev) => (prev === mod ? null : mod));
    setOpenSubModule(null);
  };

  const toggleSubModule = (sub) => {
    setOpenSubModule((prev) => (prev === sub ? null : sub));
  };

  // sel() ya no necesita estado — compara directo con la ruta activa
  const sel = (key) => activeKey === key ? "selected" : "";

  const handleLogout = () => { logout(); navigate("/login"); };
  const handleHome = () => navigate("/");

  return (
    <div className={`sidebar ${visible ? "show" : "hidden"}`}>

      {/* ── Logo ── */}
      <div className="top">
        <Link to="/" style={{ textDecoration: "none" }}>
          <span className="logo">Panel de Control</span>
        </Link>
      </div>
      <hr />

      {/* ── Nav ── */}
      <div className="center">
        <ul>
          <p className="title">Principal</p>

          {/* Inicio */}
          <li
            onClick={() => { handleHome(); }}
            className={`menu-item inicio ${sel("inicio")}`}
          >
            <DashboardIcon className="icon" />
            <span>Inicio</span>
          </li>

          {/* ── Artículos ── */}
          {menu("articulos") && (
            <ModuleItem
              icon={ArchiveIcon}
              label="Artículos"
              moduleKey="articulos"
              isOpen={openModule === "articulos"}
              onClick={() => toggleModule("articulos")}
              className="articulos"
            />
          )}
          {openModule === "articulos" && (
            <ul className="subMenu articulos">
              {can("articulo") && (
                <NavItem to="/articulos" icon={StorefrontIcon} label="Artículos"
                  selected={sel("articulosGestion")} />
              )}
              {can("ofertasCab") && (
                <NavItem to="/ofertas" icon={PriceCheckIcon} label="Ofertas"
                  selected={sel("ofertas")} />
              )}
              {can("listasCab") && (
                <NavItem to="/listas" icon={PriceCheckIcon} label="Listas"
                  selected={sel("listas")} />
              )}
              {can("inventario") && (
                <NavItem to="/inventarios" icon={InventoryIcon} label="Inventarios"
                  selected={sel("inventarios")} />
              )}

              {option("categorias") && (
                <li
                  onClick={() => toggleSubModule("categorias")}
                  className={`submenu-group ${openSubModule === "categorias" ? "open" : ""}`}
                >
                  <ListAltIcon className="icon5" />
                  <span>Categorías</span>
                  <KeyboardArrowRightIcon className={`chevron ${openSubModule === "categorias" ? "chevron--open" : ""}`} />
                </li>
              )}
              {openSubModule === "categorias" && (
                <ul className="subMenu articulos">
                  {can("ramo") && <NavItem to="/ramos" icon={CategoryIcon} label="Ramos" selected={sel("ramos")} />}
                  {can("rubro") && <NavItem to="/rubros" icon={CategoryIcon} label="Rubros" selected={sel("rubros")} />}
                  {can("subrubro") && <NavItem to="/subrubros" icon={CategoryIcon} label="SubRubros" selected={sel("subrubros")} />}
                  {can("marcas") && <NavItem to="/marcas" icon={ArticleIcon} label="Marcas" selected={sel("marcas")} />}
                </ul>
              )}

              {option("caracteristicas") && (
                <li
                  onClick={() => toggleSubModule("caracteristicas")}
                  className={`submenu-group ${openSubModule === "caracteristicas" ? "open" : ""}`}
                >
                  <ListAltIcon className="icon5" />
                  <span>Características</span>
                  <KeyboardArrowRightIcon className={`chevron ${openSubModule === "caracteristicas" ? "chevron--open" : ""}`} />
                </li>
              )}
              {openSubModule === "caracteristicas" && (
                <ul className="subMenu articulos">
                  {can("colores") && <NavItem to="/colores" icon={ColorLensIcon} label="Colores" selected={sel("colores")} />}
                  {can("medidas") && <NavItem to="/medidas" icon={StraightenIcon} label="Medidas" selected={sel("medidas")} />}
                  {can("modelos") && <NavItem to="/modelos" icon={ArticleIcon} label="Modelos" selected={sel("modelos")} />}
                  {can("presentacion") && <NavItem to="/presentaciones" icon={ArticleIcon} label="Presentaciones" selected={sel("presentaciones")} />}
                  {can("talles") && <NavItem to="/talles" icon={ArticleIcon} label="Talles" selected={sel("talles")} />}
                  {can("temporadas") && <NavItem to="/temporadas" icon={ArticleIcon} label="Temporadas" selected={sel("temporadas")} />}
                </ul>
              )}
            </ul>
          )}

          {/* ── Clientes ── */}
          {menu("clientes") && (
            <ModuleItem
              icon={FaUserFriends}
              label="Clientes"
              moduleKey="clientes"
              isOpen={openModule === "clientes"}
              onClick={() => toggleModule("clientes")}
              className="clientes"
              iconProps={{ size: 18 }}
            />
          )}
          {openModule === "clientes" && (
            <ul className="subMenu clientes">
              {can("clientes") && <NavItem to="/clientes" icon={AddBusinessIcon} label="Clientes" selected={sel("cliente")} />}
              {can("ordercache") && <NavItem to="/pedidos" icon={PlaylistAddCheckCircleIcon} label="Pedidos Recientes" selected={sel("pedidos")} />}
              {can("orderhis") && <NavItem to="/pedidosHistorial" icon={ManageSearchIcon} label="Historial Pedidos" selected={sel("historial")} />}
            </ul>
          )}

          {/* ── Empleados ── */}
          {menu("empleado") && (
            <ModuleItem
              icon={ManageAccountsIcon}
              label="Empleados"
              moduleKey="empleados"
              isOpen={openModule === "empleados"}
              onClick={() => toggleModule("empleados")}
              className="empleados"
            />
          )}
          {openModule === "empleados" && (
            <ul className="subMenu empleados">
              {can("empleados") && <NavItem to="/empleados" icon={AddBusinessIcon} label="Empleados" selected={sel("empleados_list")} />}
              {can("rol") && <NavItem to="/roles" icon={AddBusinessIcon} label="Roles" selected={sel("roles")} />}
            </ul>
          )}

          {/* ── Configuración ── */}
          {menu("configuracion") && (
            <ModuleItem
              icon={SettingsIcon}
              label="Configuración"
              moduleKey="configuracion"
              isOpen={openModule === "configuracion"}
              onClick={() => toggleModule("configuracion")}
              className="configuracion"
            />
          )}
          {openModule === "configuracion" && (
            <ul className="subMenu configuracion">
              {can("empresa") && <NavItem to={isLoadEmpresa === false ? "/empresa/new" : "/empresa/new/1"} icon={LocationCityIcon} label="Empresa" selected={sel("empresa")} />}
              {option("datosgeo") && (
                <li
                  onClick={() => toggleSubModule("datosgeo")}
                  className={`submenu-group ${openSubModule === "datosgeo" ? "open" : ""}`}

                >
                  <ListAltIcon className="icon5" />
                  <span>Datos Geográficos</span>
                  <KeyboardArrowRightIcon className={`chevron ${openSubModule === "datosgeo" ? "chevron--open" : ""}`} />
                </li>
              )}
              {openSubModule === "datosgeo" && (
                <ul className="subMenu">
                  {can("provincias") && <NavItem to="/provincias" icon={WhereToVoteIcon} label="Provincias" selected={sel("provincias")} />}
                  {can("provincias") && <NavItem to="/departamentos" icon={WhereToVoteIcon} label="Departamentos" selected={sel("departamentos")} />}
                  {can("localidades") && <NavItem to="/localidades" icon={WhereToVoteIcon} label="Localidades" selected={sel("localidades")} />}
                  {can("barrios") && <NavItem to="/barrios" icon={WhereToVoteIcon} label="Barrios" selected={sel("barrios")} />}
                </ul>
              )}
              {option("establecimientos") && (
                <li
                  onClick={() => toggleSubModule("establecimientos")}
                  className={`submenu-group ${openSubModule === "establecimientos" ? "open" : ""}`}

                >
                  <ListAltIcon className="icon5" />
                  <span>Establecimientos</span>
                  <KeyboardArrowRightIcon className={`chevron ${openSubModule === "categorias" ? "chevron--open" : ""}`} />
                </li>
              )}
              {openSubModule === "establecimientos" && (
                <ul className="subMenu">
                  {can("sucursales") && <NavItem to="/sucursales" icon={AddBusinessIcon} label="Sucursales" selected={sel("sucursales")} />}
                  {can("deposito") && <NavItem to="/depositos" icon={AddBusinessIcon} label="Depósitos" selected={sel("depositos")} />}

                </ul>
              )}</ul>
          )}

          {/* ── CMS ── */}
          {menu("cms") && (
            <ModuleItem
              icon={WebIcon}
              label="CMS"
              moduleKey="cms"
              isOpen={openModule === "cms"}
              onClick={() => toggleModule("cms")}
              className="webecommerce"
            />
          )}

          {/* ── Informes ── */}
          {menu("informes") && (
            <ModuleItem
              icon={HiOutlineDocumentReport}
              label="Informes"
              moduleKey="informes"
              isOpen={openModule === "informes"}
              onClick={() => toggleModule("informes")}
              className="informes"
              iconProps={{ size: 20 }}
            />
          )}
          {openModule === "informes" && (
            <ul className="subMenu informes">
              <NavItem to="/existencias" icon={FaBoxes} label="Existencias"
                selected={sel("existencias")}
                iconProps={{ size: 17 }} />
            </ul>
          )}

          {/* ── Web Ecommerce ── */}
          {menu("web") && (
            <ModuleItem
              icon={ImportantDevicesIcon}
              label="Web Ecommerce"
              moduleKey="webecommerce"
              isOpen={openModule === "webecommerce"}
              onClick={() => toggleModule("webecommerce")}
              className="webecommerce"
            />
          )}
          {openModule === "webecommerce" && (
            <ul className="subMenu webecommerce">
              {can("themes") && <NavItem to="/theme" icon={ViewDayIcon} label="Tema" selected={sel("theme")} />}
              {can("banners") && <NavItem to="/banners" icon={ViewDayIcon} label="Banners" selected={sel("banners")} />}
              {can("sliders") && <NavItem to="/sliders" icon={ViewCarouselIcon} label="Sliders" selected={sel("sliders")} />}
              {can("descuentos") && <NavItem to="/descuentos" icon={DiscountIcon} label="Descuentos" selected={sel("descuentos")} />}
            </ul>
          )}

          {/* ── Web Preventa ── */}
          {menu("preventa") && (
            <ModuleItem
              icon={PhonelinkSetupIcon}
              label="Web Preventa"
              moduleKey="webpreventa"
              isOpen={openModule === "webpreventa"}
              onClick={() => toggleModule("webpreventa")}
              className="webpreventa"
            />
          )}
          {openModule === "webpreventa" && (
            <ul className="subMenu webpreventa">
              {can("visitas") && <NavItem to="/visitas" icon={PersonPinCircleIcon} label="Visitas Preventistas" selected={sel("visitas")} />}
              {can("rutas") && <NavItem to="/visitasmaps" icon={RoundaboutRightIcon} label="Ruteo de Preventistas" selected={sel("rutas")} />}
            </ul>
          )}

          {/* ── Salir ── */}
          <li onClick={handleLogout} className="menu-item salir" style={{ marginTop: 16 }}>
            <ExitToAppIcon className="icon" />
            <span>Salir</span>
          </li>
        </ul>
      </div>

      {/* ── Theme switcher ── */}
      <div className="bottom">
        <div className="colorOption" onClick={() => dispatch({ type: "LIGHT" })} />
        <div className="colorOption" onClick={() => dispatch({ type: "DARK" })} />
      </div>
    </div>
  );
}

export default Sidebar;